/**
 * Where your agents are, drawn (RFC 0336 3f, S10).
 *
 * Between the World agent's thread and the box, so it is the last thing read
 * before typing. One line a session: its dot, its mark, its name, and what it
 * is asking, doing or last said. ↑ from the empty box walks them (WorldView
 * owns the keys); a selected question opens above its line as taps answered
 * here without moving the window, and ↵ goes to any line for the rest.
 */

import { Check } from 'lucide-react'
import {
  Fragment,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { QuestionTaps } from '../../features/thread/SessionActivity'
import { useQuestionTaps } from '../../features/thread/useQuestionTaps'
import { SessionMark } from '../../services/sessions/SessionMark'
import { lineAge, WORLD_LINE_LIMIT, type WorldLine } from './liveLines'
import { StatusDot } from './StatusDot'

/** One line's height: the list is `WORLD_LINE_LIMIT` of these tall. */
const ROW_PX = 28
/** How often a line's age is re-read while on screen. */
const AGE_TICK_MS = 30_000

export function WorldLines({
  lines,
  selected,
  onPick,
  numberKeyRef,
  live = true,
  confirming = null,
  onAskDone,
  onDone,
  onWant,
}: {
  lines: readonly WorldLine[]
  /** The line ↑ has walked to, or null while the box has the keys. */
  selected: number | null
  /** Put through to it (RFC 0336 3d). */
  onPick: (line: WorldLine) => void
  /** Where WorldView sends 1-9 while a line is selected: true if it answered. */
  numberKeyRef?: RefObject<((n: number) => boolean) | null>
  /** On screen: ages tick only while it is. */
  live?: boolean
  /** The session waiting on "file as done?", asked by ⌫ or the check. */
  confirming?: string | null
  /** Ask to file it as done: the first ⌫ or the first click on the check. */
  onAskDone?: (line: WorldLine) => void
  /** Confirmed: file it as done (RFC 0286). */
  onDone?: (line: WorldLine) => void
  /** The lines in view (scrolled to, or hovered), so their text is read: the
   *  first few are read anyway, the rest only when someone looks. */
  onWant?: (sessionIds: string[]) => void
}) {
  const line = selected !== null ? lines[selected] : undefined
  const question = line?.question
  // The same taps the composer strip uses: one answer per question.
  const { taps, onTap, onSkip, sent } = useQuestionTaps(
    line?.sessionId,
    line?.projectId,
    question,
  )

  // AGES MOVE while the lines are on screen: "now" must become "1m" without
  // anything else changing. A minute's tick is the smallest step drawn.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!live) return
    setNow(Date.now())
    const id = setInterval(() => setNow(Date.now()), AGE_TICK_MS)
    return () => clearInterval(id)
  }, [live])

  // 1-9 picks that option of the question on screen.
  useEffect(() => {
    if (!numberKeyRef) return
    numberKeyRef.current = (n) => {
      // Answered already: the number is not an answer any more.
      if (sent) return false
      const options = question?.body.questions[taps.step]?.options
      const option = options?.[n - 1]
      if (!option) return false
      onTap(option.value)
      return true
    }
    return () => {
      numberKeyRef.current = null
    }
  }, [numberKeyRef, question, taps.step, onTap, sent])

  // The selected line sits at the bottom of the list, right above the box,
  // with what it said above it; the lines after it scroll out below.
  const selectedRef = useRef<HTMLButtonElement>(null)
  // Opens at the bottom, where the latest are, and stays there as lines
  // arrive, until ↑ has picked one.
  const listRef = useRef<HTMLDivElement>(null)
  const count = lines.length
  // biome-ignore lint/correctness/useExhaustiveDependencies: `count` is the trigger; the effect reads the element
  useLayoutEffect(() => {
    if (selected !== null) return
    const el = listRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [count, selected])
  const selectedId = line?.sessionId
  // Again when its question arrives: the taps grow the space above.
  const opened = line?.question?.interactionId
  // biome-ignore lint/correctness/useExhaustiveDependencies: `opened` is the trigger; the effect reads the element
  useLayoutEffect(() => {
    if (!selectedId) return
    // Feature-detected: jsdom has no layout and no scrollIntoView.
    selectedRef.current?.scrollIntoView?.({ block: 'end' })
  }, [selectedId, opened])

  // A SELECTED QUESTION, OPENED as taps, drawn above its line so the line
  // stays on the box. Any other line stays one line: its status and summary
  // are the glance, and ↵ goes there for the rest.
  const detail = line?.question ? (
    <div className="ml-6 px-2 pt-1.5 pb-2 text-xs">
      <QuestionTaps
        body={line.question.body}
        taps={taps}
        onTap={onTap}
        onSkip={onSkip}
      />
    </div>
  ) : null

  if (lines.length === 0) return null
  return (
    <section className="flex w-full flex-col pb-1" aria-label="Your agents">
      {/* Every session not filed, the ones that need you and the latest at
          the bottom by the box; five rows tall, the rest a scroll away. */}
      <div
        ref={listRef}
        onScroll={(e) => {
          if (!onWant) return
          // Rows in view, plus one each side: read before they are reached.
          const first = Math.floor(e.currentTarget.scrollTop / ROW_PX) - 1
          onWant(
            lines
              .slice(Math.max(0, first), first + WORLD_LINE_LIMIT + 2)
              .map((l) => l.sessionId),
          )
        }}
        className="scroll-quiet overflow-y-auto overflow-x-hidden"
        style={{
          maxHeight: detail
            ? `calc(${WORLD_LINE_LIMIT * ROW_PX}px + 30vh)`
            : `${WORLD_LINE_LIMIT * ROW_PX}px`,
        }}
      >
        {lines.map((l, i) => {
          const isSelected = i === selected
          return (
            <Fragment key={l.sessionId}>
              {isSelected ? detail : null}
              <div
                style={{ minHeight: `${ROW_PX}px` }}
                className={`group/line flex w-full min-w-0 items-center rounded-lg ${
                  isSelected
                    ? 'bg-foreground/10 text-foreground'
                    : 'hover:bg-foreground/[0.05]'
                }`}
              >
                <button
                  ref={isSelected ? selectedRef : undefined}
                  type="button"
                  onClick={() => onPick(l)}
                  onMouseEnter={() => onWant?.([l.sessionId])}
                  // Hover reads the whole last message without selecting.
                  title={l.full ?? `${l.title} · ${l.projectName}`}
                  style={{ minHeight: `${ROW_PX}px` }}
                  className="flex min-w-0 flex-1 items-center gap-2 px-2 text-left text-[13px]"
                >
                  <span className="flex-none">
                    {l.state === 'done' || l.state === 'waiting' ? (
                      // Quiet: a ring, no motion. A result you have not seen
                      // yet is a brighter ring than work waiting on a move.
                      <span
                        className={`block size-1.5 rounded-full border ${
                          l.state === 'done'
                            ? 'border-foreground/70'
                            : 'border-muted-foreground/60'
                        }`}
                      />
                    ) : (
                      <StatusDot status={l.state} />
                    )}
                  </span>
                  {/* Its mark, as every session has one (RFC 0311): the
                    quickest way to tell two sessions apart. */}
                  <SessionMark id={l.sessionId} size={14} />
                  {/* Capped and truncated: a long name used to push the row
                      wider than the list, which scrolled sideways and cut
                      every row's first letters. The full name is the title. */}
                  <span className="min-w-0 max-w-[60%] shrink-0 truncate font-medium text-foreground">
                    {l.title}
                  </span>
                  <span
                    className={`min-w-0 flex-1 truncate ${
                      confirming === l.sessionId
                        ? 'text-foreground'
                        : l.state === 'done'
                          ? 'text-foreground/70'
                          : 'text-muted-foreground'
                    }`}
                  >
                    {confirming === l.sessionId
                      ? 'File as done?'
                      : l.state === 'asking' && isSelected && l.question
                        ? 'asking you'
                        : l.text}
                  </span>
                  <span className="flex-none text-[11px] text-muted-foreground">
                    {lineAge(l.updatedAt, now)}
                  </span>
                  {confirming === l.sessionId && isSelected ? (
                    <kbd className="flex-none rounded border border-border px-1 font-sans text-[11px] text-foreground">
                      ↵ confirm
                    </kbd>
                  ) : isSelected ? (
                    <kbd className="flex-none rounded border border-border px-1 font-sans text-[11px] text-muted-foreground">
                      ↵ go there · ⌫ done
                    </kbd>
                  ) : null}
                </button>
                {/* FILE AS DONE, on hover: one click asks, the second files
                    it, the same two steps as ⌫ then ↵. */}
                {onDone ? (
                  <button
                    type="button"
                    aria-label={
                      confirming === l.sessionId
                        ? `File ${l.title} as done`
                        : `Done with ${l.title}`
                    }
                    title={
                      confirming === l.sessionId
                        ? 'Click again to file as done'
                        : 'File as done'
                    }
                    onClick={() =>
                      confirming === l.sessionId ? onDone(l) : onAskDone?.(l)
                    }
                    className={`mr-1 flex-none items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] ${
                      confirming === l.sessionId
                        ? 'flex bg-green-500/15 text-green-500'
                        : 'hidden text-muted-foreground hover:text-foreground group-hover/line:flex'
                    }`}
                  >
                    <Check className="size-3.5" strokeWidth={2.5} />
                    {confirming === l.sessionId ? <span>Done</span> : null}
                  </button>
                ) : null}
              </div>
            </Fragment>
          )
        })}
      </div>
    </section>
  )
}
